import type * as React from 'react';

/**
 * One suggestion in a Composer typeahead. The consumer supplies these (already
 * filtered + sorted) for the current query; the Composer renders + inserts.
 */
export interface ComposerMentionItem {
  /** Stable id — the React key, and passed back to `onSelect`. */
  id: string;
  /**
   * Shown in the row AND inserted into the text as `${trigger}${label} `.
   *
   * Must be a run of the characters this trigger allows: letters, digits and
   * underscore by default, plus whatever {@link ComposerMentions.labelChars}
   * adds. Mentions serialise into the plain-text `value` as `${trigger}${label}`
   * and are re-detected from it on any rebuild, so a label containing anything
   * outside that set (a space, most obviously) would round-trip back to plain
   * text and lose its chip.
   */
  label: string;
  /** Optional leading node (e.g. an avatar dot / icon). */
  icon?: React.ReactNode;
}

/**
 * Typeahead configuration for {@link Composer}. Turns a Composer into a
 * `@mention`-style input: the Composer owns the trigger detection, keyboard
 * model, positioning, a11y, and text insertion; the consumer owns the data
 * (which items match a query) and any side effect on select.
 *
 * A Composer may carry **several** of these — one per trigger char — e.g. `@`
 * for agents and `/` for files.
 */
export interface ComposerMentions {
  /** Character that opens the typeahead. Single char; defaults to `'@'`. */
  trigger?: string;
  /**
   * Characters allowed inside this trigger's labels/queries **beyond** letters,
   * digits and `_` — supplied as literal chars, e.g. `'.-'` for filenames.
   *
   * It widens BOTH halves in lockstep: the live query (so typing
   * `/q3-report.md` keeps filtering past the `-`) and the committed-chip
   * detection (so the same token rebuilds as a chip). Widen per trigger, never
   * globally — `@` must not start matching `@some.thing`. Whitespace can never
   * be added: it is what ends a mention.
   */
  labelChars?: string;
  /**
   * Items to show for the current query (the text typed after the trigger, up
   * to the caret). Empty query means "show all". The consumer filters + sorts;
   * returning `[]` closes the list.
   */
  getItems: (query: string) => readonly ComposerMentionItem[];
  /** Side effect when an item is committed, after the text has been inserted. */
  onSelect?: (item: ComposerMentionItem) => void;
  /** Accessible label for the suggestion listbox. Defaults to `'Suggestions'`. */
  listLabel?: string;
}

/** A run of composer text, flagged as a committed mention token or plain text. */
export interface MentionSegment {
  text: string;
  mention: boolean;
}

/** One resolved trigger: its char, its label charset, and what counts as known. */
export interface MentionMatcher {
  /** The trigger character (already defaulted — never undefined here). */
  trigger: string;
  /** Extra label chars beyond `\p{L}\p{N}_`, as literal chars. */
  labelChars?: string;
  /** Whether `word` (the text after the trigger) names a real item. */
  isMention: (word: string) => boolean;
}

/** Chars every mention label may contain, whatever the trigger. */
const BASE_LABEL_CHARS = '\\p{L}\\p{N}_';

/**
 * Regex character-class BODY for one trigger's label — the single definition
 * of "what can appear in a mention", shared by the live trigger match and the
 * committed-chip segmentation so the two can never disagree about where a
 * token ends.
 */
export function labelCharClass(labelChars = ''): string {
  return BASE_LABEL_CHARS + escapeCharClass(labelChars);
}

/**
 * Split `text` into plain + mention runs, so the editor can build committed
 * mentions (`@Label`, `/file.md`) as atomic chip nodes and plain text as text
 * nodes. A run `${trigger}word` is a mention iff it is word-initial (start /
 * after whitespace) AND the trigger's own `isMention(word)` — i.e. the word
 * matches a known item, so a half-typed or bogus `@foo` is left as plain text.
 *
 * All triggers are matched in ONE pass over the text. Segmenting per trigger
 * and re-segmenting the leftovers would be shorter, but each pass would see a
 * fragment's start as word-initial when it is mid-word in the real string
 * (`@Architect/foo` would yield a bogus `/foo` chip). Pure + synchronous.
 */
export function segmentMentions(
  text: string,
  matchers: readonly MentionMatcher[],
): MentionSegment[] {
  if (matchers.length === 0) return text.length > 0 ? [{ text, mention: false }] : [];
  const segments: MentionSegment[] = [];
  // LONGEST TRIGGER FIRST, and it matters twice: regex alternation is
  // first-wins, and the matcher is recovered below by `startsWith`. With the
  // two triggers in use (`@`, `/`) order is irrelevant — but any overlapping
  // pair added later (`@` / `@@`) would otherwise resolve to the shorter one's
  // `isMention` and slice off the wrong prefix, silently. Sorting is cheaper
  // than the indexed-group plumbing that would also fix it.
  const ordered = [...matchers].sort((a, b) => b.trigger.length - a.trigger.length);
  // Word-initial trigger + a run of that trigger's label chars, alternated
  // across every configured trigger.
  const alternatives = ordered
    .map((m) => `${escapeRegExp(m.trigger)}[${labelCharClass(m.labelChars)}]+`)
    .join('|');
  const re = new RegExp(`(^|\\s)(${alternatives})`, 'gu');
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const at = m.index + m[1]!.length; // index of the trigger char
    const token = m[2]!; // e.g. "@Architect" / "/plan.md"
    const matcher = ordered.find((c) => token.startsWith(c.trigger));
    if (matcher && matcher.isMention(token.slice(matcher.trigger.length))) {
      if (at > last) segments.push({ text: text.slice(last, at), mention: false });
      segments.push({ text: token, mention: true });
      last = at + token.length;
    }
  }
  if (last < text.length) segments.push({ text: text.slice(last), mention: false });
  return segments;
}

/** Escape literal chars for use in a regex (shared with the editor's trigger match). */
export function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Escape literal chars for use INSIDE a `[...]` class (`-` and `]` bite there). */
function escapeCharClass(s: string): string {
  return s.replace(/[\\\]^-]/g, '\\$&');
}

/**
 * Whether a mention token dropped between `before` and `after` needs padding
 * spaces to survive as a chip.
 *
 * A mention is word-initial by definition ({@link segmentMentions}), so a token
 * landing straight after a word rebuilds as PLAIN TEXT — the chip silently
 * disappears on the next value sync. The trailing space is where the caret
 * lands, so the user carries on typing after the chip rather than inside it.
 *
 * Pure, because the caret resolution around it is not testable outside a real
 * browser — this is the part with a rule in it.
 */
export function mentionPadding(
  before: string,
  after: string,
): { leading: boolean; trailing: boolean } {
  return {
    // Start-of-message needs nothing; mid-word needs a space or the chip dies.
    leading: before !== '' && !/\s$/.test(before),
    // Includes end-of-message (`after === ''`), which is where the caret goes.
    trailing: !/^\s/.test(after),
  };
}
